import { shallowRef } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import usePageDataStore from './usePageData';
import { fireLinkTag, fireViewTag } from '~/features/analytics/tagManager';
import { combinePayloads } from '~/utils/combinePayloads';

type Options = { once: boolean };

type Tag = {
  event_name?: string;
  link_name?: string;
};

type ExperimentTag = {
  event_name?: string;
  experiment_key?: string;
  experiment_key_type?: string;
};
//@typescript-eslint/no-empty-object-type
export interface TagData extends Partial<Tag & ExperimentTag> {} // eslint-disable-line

export type ExperimentTagDataStructure = {
  recipeIds: Array<string>;
  data: ExperimentTag;
};

export type AnalyticsTagDataStructure = {
  action: string;
  data: Tag;
  experiment: ExperimentTagDataStructure;
};

const useAnalyticsStore = defineStore('analytics', () => {
  const { pageData } = storeToRefs(usePageDataStore());

  const tagCallsCache = shallowRef<{ link: Set<string>; view: Set<string> }>({
    link: new Set(),
    view: new Set(),
  });

  function createTagHandler(isLinkTag: boolean) {
    const fireAnalyticsTag = isLinkTag ? fireLinkTag : fireViewTag;
    const staticPayloads = pageData.value?.analyticsTags?.[isLinkTag ? 'linkTag' : 'viewTag'] ?? {};
    const cache = tagCallsCache.value[isLinkTag ? 'link' : 'view'];

    return function (action: string, customPayload?: object, options: Options = { once: false }) {
      if (options.once && cache.has(action)) {
        return;
      }

      const staticPayload = staticPayloads?.[action];

      if (!staticPayload && !customPayload) {
        return;
      }

      if (options.once) {
        cache.add(action);
      }

      fireAnalyticsTag(combinePayloads(staticPayload, customPayload));
    };
  }

  const _fireLinkTag = createTagHandler(true);
  const _fireViewTag = createTagHandler(false);

  function fireLinkTagAction(action: string, customPayload?: object) {
    _fireLinkTag(action, customPayload);
  }

  function fireViewTagAction(action: string, customPayload?: object) {
    _fireViewTag(action, customPayload);
  }

  function fireLinkTagOnce(action: string, customPayload?: object) {
    _fireLinkTag(action, customPayload, { once: true });
  }

  function fireViewTagOnce(action: string, customPayload?: object) {
    _fireViewTag(action, customPayload, { once: true });
  }

  return {
    tagCallsCache,
    fireLinkTag: fireLinkTagAction,
    fireViewTag: fireViewTagAction,
    fireLinkTagOnce,
    fireViewTagOnce,
  };
});

export default useAnalyticsStore;
